import { ToolDefinition } from "../types";

const decryptTextOnline: ToolDefinition = {
  slug: "decrypt-text-online",
  name: "Decrypt Text Online | AES-256-GCM & ChaCha20 Decryptor",
  title: "Decrypt Text Online",
  description:
    "Securely decrypt text online using AES-256-GCM and ChaCha20 algorithms. Paste your base64 ciphertext and secret key to instantly recover the original plaintext.",
  longDescription:
    "Easily recover plaintext from supported AES-256-GCM and ChaCha20 cipher payloads. Simply provide your base64-encoded encrypted string alongside the original secret key to instantly reverse the encryption and reveal your message.",
  toolType: {
    label: "Text Decryptor",
    icon: "KeyRound",
  },
  heroBadges: [
    "100% Free",
    "No Account Required",
    "AES-256-GCM Support",
    "ChaCha20 Support",
    "Instant Decryption",
  ],
  category: "Security Tools",
  categorySlug: "security-tools",
  href: "/tools/decrypt-text-online",
  api: "/api/v1/decrypt",
  keywords: [
    "snappy text decryption tool",
    "decrypt message",
    "aes cipher decoder",
    "aes 256 gcm recovery",
    "chacha20 plaintext tool",
    "text decryptor",
    "string decryption app",
    "base64 cipher decoder",
    "recover plaintext online",
    "secret key decryptor",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Decrypt Text Online",
        description:
          "Follow these simple steps to recover plaintext from your encrypted base64 payload.",
      },
      benefits: {
        title: "Why Use Our Text Decryptor",
        description:
          "A reliable, straightforward way to reverse authenticated encryption and verify your sensitive messages.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        description:
          "Learn more about text decryption, supported algorithms, and common troubleshooting tips.",
      },
    },
    intro: {
      title: "Reliable and Secure Text Decryption",
      paragraphs: [
        "When dealing with modern authenticated encryption, you need a reliable way to verify outputs, troubleshoot code, or simply recover a secure message. Our decrypt text online tool allows you to input ciphertext generated by popular symmetric algorithms like AES-256-GCM and ChaCha20 to quickly recover plaintext online.",
        "To successfully decrypt a message, you must possess the original secret key used during the encryption process. This text decryptor is designed to handle base64-encoded payloads that bundle both the required initialization vector (often called a nonce) and the ciphertext itself, making it easy to test and validate secure communication channels.",
      ],
      note: "This tool strictly performs symmetric decryption for supported cipher payloads. It cannot crack passwords or decrypt hashes.",
    },
    introBlocks: [
      {
        kind: "text",
        title: "Understanding Symmetric Decryption",
        level: "h2",
        paragraphs: [
          "In symmetric encryption, the exact same secret key is used for both locking and unlocking the data. When you want to decrypt string online using algorithms like AES or ChaCha20, the mathematical process simply runs the encryption steps in reverse. However, this is only possible if the input data is structurally perfect.",
          "Modern protocols, particularly AES 256 GCM decryption and ChaCha20 decryption, are 'authenticated' ciphers. This means they not only encrypt the data but also generate an authentication tag to ensure the ciphertext hasn't been tampered with. If the base64 decrypt text payload is modified even slightly, or if you use the wrong key, the decryption will immediately fail to protect the integrity of the data.",
        ],
        note: "Ensure your base64 string includes the 12-byte nonce prepended to the ciphertext.",
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "Key Requirements for Successful Decryption",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "Accurate Base64 Input",
                icon: "Binary",
                description:
                  "Your input must be a valid base64 encoded string containing the original payload.",
                accent: "primary",
              },
              {
                label: "Exact Secret Key",
                icon: "Key",
                description:
                  "You must provide the exact secret key used to encrypt the message. Close matches will fail.",
                accent: "primary",
              },
              {
                label: "Nonce Inclusion",
                icon: "ShieldAlert",
                description:
                  "The payload must contain a 12-byte nonce standard in GCM and Poly1305 operations.",
                accent: "primary",
              },
            ],
          },
        ],
      },
      {
        kind: "table",
        tables: [
          {
            title: "Encryption vs. Hashing",
            description:
              "Understanding the difference between reversible ciphers and one-way hashes.",
            columns: [
              "Feature",
              "Symmetric Encryption (AES/ChaCha)",
              "Hashing (SHA-256/MD5)",
            ],
            rows: [
              {
                Feature: "Reversibility",
                "Symmetric Encryption (AES/ChaCha)":
                  "Yes, with the correct key",
                "Hashing (SHA-256/MD5)": "No, completely one-way",
              },
              {
                Feature: "Key Requirement",
                "Symmetric Encryption (AES/ChaCha)": "Requires a secret key",
                "Hashing (SHA-256/MD5)": "No key required (usually)",
              },
              {
                Feature: "Output Size",
                "Symmetric Encryption (AES/ChaCha)":
                  "Scales with the input data length",
                "Hashing (SHA-256/MD5)": "Fixed length regardless of input",
              },
              {
                Feature: "Primary Use Case",
                "Symmetric Encryption (AES/ChaCha)":
                  "Secure messaging and data storage",
                "Hashing (SHA-256/MD5)":
                  "Password verification and file integrity",
              },
            ],
            note: "This explains why hashes cannot be decrypted by this or any other tool.",
            emphasisColumns: ["Feature"],
            monospaceColumns: [],
          },
        ],
      },
    ],
    callouts: [
      {
        title: "Decryption Limitations",
        layout: "list",
        iconColor: "primary",
        items: [
          {
            label: "No Password Cracking",
            icon: "ShieldX",
            description:
              "This tool does not brute force or recover lost passwords. You must know the key.",
            accent: "primary",
          },
          {
            label: "Reversible Ciphers Only",
            icon: "ArrowLeftRight",
            description:
              "We only support reversible text decryption. Hashes like SHA256 cannot be reversed.",
            accent: "primary",
          },
        ],
      },
    ],
    referenceTables: [
      {
        title: "Supported Decryption Algorithms",
        description:
          "A breakdown of the specific cipher specifications supported by our tool.",
        columns: ["Algorithm", "Type", "Nonce Size", "Tag Size"],
        rows: [
          {
            Algorithm: "AES-256-GCM",
            Type: "Authenticated Block Cipher",
            "Nonce Size": "12 Bytes",
            "Tag Size": "16 Bytes",
          },
          {
            Algorithm: "ChaCha20-Poly1305",
            Type: "Authenticated Stream Cipher",
            "Nonce Size": "12 Bytes",
            "Tag Size": "16 Bytes",
          },
        ],
        note: "Both algorithms require the initialization vector to be prepended to the ciphertext before base64 encoding.",
        emphasisColumns: ["Algorithm"],
        monospaceColumns: [],
      },
    ],
    faqs: [
      {
        question: "How do I decrypt text online?",
        answer:
          "To decrypt text online, you need two things: the encrypted message (ciphertext) formatted as base64, and the secret key used to lock it. Simply paste your base64 text into the input field, select the correct algorithm (like AES-256-GCM), enter your secret key, and click decrypt. If the details match, the plaintext will be revealed.",
      },
      {
        question: "What key do I need to decrypt AES-256-GCM text?",
        answer:
          "You need the exact symmetric secret key that was generated and used during the encryption phase. Without this specific key, it is mathematically impossible to recover plaintext from the encrypted text. AES-256 requires a 256-bit (32-byte) key.",
      },
      {
        question: "Can ChaCha20 encrypted text be decrypted?",
        answer:
          "Yes, ChaCha20 is a symmetric stream cipher, meaning it is fully reversible. As long as you have the original base64 payload and the correct secret key, our tool can perform ChaCha20 decryption online and return your original message.",
      },
      {
        question: "Why can hashes not be decrypted?",
        answer:
          "Hashes (like SHA-256 or MD5) are designed to be one-way mathematical functions. They scramble data into a fixed-length string, losing the original information in the process. Unlike AES or ChaCha20, hashing does not use a reversible key, which is why hashes cannot be decrypted back into plaintext.",
      },
      {
        question: "What does nonce plus ciphertext mean?",
        answer:
          "In modern authenticated encryption algorithms, a 'nonce' (number used once) or Initialization Vector (IV) is used to randomize the encryption, ensuring that encrypting the same message twice yields different results. To decrypt base64 ciphertext online, the standard format requires the 12-byte nonce to be attached directly at the beginning of the ciphertext before it is encoded into base64.",
      },
      {
        question: "Why does decryption fail with the wrong key?",
        answer:
          "Because AES-GCM and ChaCha20-Poly1305 are authenticated ciphers, they use an authentication tag to verify data integrity. If you enter the wrong key, or if the ciphertext is modified, the mathematical validation fails immediately. The tool will throw an error rather than outputting corrupted or false plaintext.",
      },
      {
        question:
          "What is the difference between decrypting and decoding Base64?",
        answer:
          "Decoding Base64 simply translates data from one readable format to another; it provides no security and requires no password. Decrypting, however, unlocks a secure algorithmic scramble (like AES) using a secret mathematical key. Our tool accepts decoded base64 input, but performs cryptographic text decryption on the underlying bytes.",
      },
    ],
    benefits: [
      {
        icon: "CheckCircle2",
        title: "Accurate Plaintext Recovery",
        description:
          "Recovers your original message flawlessly, provided you input the correct base64 payload and matching secret key.",
      },
      {
        icon: "Shield",
        title: "Modern Algorithm Support",
        description:
          "Supports industry-standard authenticated encryption, allowing for secure aes 256 gcm decrypt text online and ChaCha20.",
      },
      {
        icon: "Zap",
        title: "Fast and Reliable",
        description:
          "Process complex cryptographic operations instantly right in your browser, with clear error messages if a key or format is incorrect.",
      },
      {
        icon: "Binary",
        title: "Base64 Native",
        description:
          "Built to accept standard base64 formatted cipher payloads, making it easy to test outputs directly from your application code.",
      },
    ],
    steps: [
      {
        number: "01",
        title: "Paste Cipher Text",
        description:
          "Enter the base64-encoded encrypted string into the text area. Ensure it includes the standard 12-byte nonce.",
      },
      {
        number: "02",
        title: "Select Algorithm",
        description:
          "Choose the matching encryption method, either AES-256-GCM or ChaCha20, from the provided dropdown menu.",
      },
      {
        number: "03",
        title: "Enter Secret Key",
        description:
          "Type or paste the exact symmetric secret key that was originally used to encrypt the payload.",
      },
      {
        number: "04",
        title: "Decrypt and Copy",
        description:
          "Submit your request. If the key and payload are valid, the tool will recover the plaintext for you to copy and use.",
      },
    ],
    useCases: [
      {
        label: "Code Troubleshooting",
        icon: "Terminal",
        description:
          "Developers can verify that their application's encryption logic is outputting valid, decryptable payloads.",
        accent: "primary",
      },
      {
        label: "Message Recovery",
        icon: "MessageSquare",
        description:
          "Safely read sensitive information that was transmitted to you in an encrypted format using a shared secret key.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Software Developers",
        icon: "Code",
        description:
          "For testing and validating cryptographic implementations in new applications.",
        accent: "accent",
      },
      {
        label: "Security Researchers",
        icon: "ShieldCheck",
        description:
          "For analyzing structured ciphertext formats and validating known cryptographic outputs.",
        accent: "accent",
      },
    ],
  },
};

export default decryptTextOnline;
export { decryptTextOnline };
